import { StepResult, ProjectData, FinancialParams } from '../types';

export const generateHtmlReport = (projectData: ProjectData, steps: StepResult[], financialParams: FinancialParams | null): string => {
  // 1. Locate the text body for the report (The "IC Memo")
  // Typically Step 7 (Index 8). If not found, fall back to the last step.
  const memoStep = steps.find(s => s.stepId === 8) || steps[steps.length - 1];
  
  // 2. Locate the data source for metrics (The "System Data Block")
  // Typically Step 6 (Index 7), but we search all steps in reverse to find the latest data block.
  // We look for the marker [[SYSTEM_DATA_START]]
  const dataStep = [...steps].reverse().find(s => s.content.includes('[[SYSTEM_DATA_START]]')) || 
                   steps.find(s => s.stepId === 7) || 
                   memoStep; // Fallback to memoStep if no data block found anywhere
  
  // Filter out the memo step from the "supporting" steps list to avoid duplication in tabs
  const supportingSteps = steps.filter(s => s !== memoStep);
  
  // Extract metrics from the identified data source content
  const extractMetric = (key: string, content: string, fallback: string = "N/A") => {
    // Matches [Key]: Value
    const regex = new RegExp(`\\[${key}\\]:\\s*([^\\[\\n]+)`, 'i');
    const match = content.match(regex);
    if (match) {
        return match[1].trim();
    }
    // Fallback: try matching without brackets if model failed to format
    const looseRegex = new RegExp(`${key}:\\s*([^\\[\\n]+)`, 'i');
    const looseMatch = content.match(looseRegex);
    return looseMatch ? looseMatch[1].trim() : fallback;
  };

  // Use dataStep.content for metrics extraction
  const sourceContent = dataStep ? dataStep.content : "";

  const noi = extractMetric("NOI", sourceContent);
  const revenueY3 = extractMetric("RevenueY3", sourceContent);
  const totalInvest = extractMetric("TotalInvestment", sourceContent);
  const efficiency = extractMetric("Efficiency", sourceContent);
  const irr = extractMetric("IRR", sourceContent);
  const payback = extractMetric("PaybackPeriod", sourceContent);
  
  // New metrics for Light Asset / detailed view
  const ownerIncomeY3 = extractMetric("OwnerIncomeY3", sourceContent, "");
  const ownerTotal = extractMetric("OwnerTotalIncome", sourceContent, "");
  const managerIncomeY3 = extractMetric("ManagerIncomeY3", sourceContent, "");
  const managerTotal = extractMetric("ManagerTotalIncome", sourceContent, "");

  // Safe formatting for currency values
  const formatValue = (val: string, targetUnit: string) => {
    if (!val || val === "N/A" || val === "0") return val;

    // 1. Remove commas and existing unit text to get raw number
    const numericStr = val.replace(/[^0-9.-]/g, '');
    const num = parseFloat(numericStr);
    
    // If not a number, return original
    if (isNaN(num)) return val; 

    // 2. Logic for "万元" conversion
    if (targetUnit === '万元' || targetUnit === '万元/年') {
        // If the number is > 10,000, it's likely raw Yuan.
        if (num > 10000) {
            const wanVal = (num / 10000).toFixed(0); 
            return `${wanVal} <span style="font-size: 0.6em; color: #64748b;">万元</span>`;
        }
    }

    // 3. Logic for Percentage
    if (targetUnit === '%') {
        if (val.includes('%')) return val; 
        if (Math.abs(num) <= 1 && num !== 0) {
            return `${(num * 100).toFixed(2)}<span style="font-size: 0.6em; color: #64748b;">%</span>`;
        }
        return `${val}<span style="font-size: 0.6em; color: #64748b;">%</span>`;
    }
    
    const cleanUnit = targetUnit.replace('/', '');
    if (val.includes(cleanUnit)) return val;

    return `${val} <span style="font-size: 0.6em; color: #64748b;">${targetUnit}</span>`;
  };

  // Construct Image HTML if base64 exists
  const imageHtml = projectData.designImageBase64 
    ? `
      <div style="margin-top: 2rem; border-top: 2px solid #e2e8f0; padding-top: 2rem;">
        <h3 style="color: #1e293b; font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem;">附录：用户上传设计图纸 (Floor Plan)</h3>
        <div style="background: #f8fafc; padding: 1rem; border-radius: 0.5rem; border: 1px solid #e2e8f0; text-align: center;">
            <img src="data:image/png;base64,${projectData.designImageBase64}" style="max-width: 100%; max-height: 800px; border-radius: 0.25rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);" alt="Project Floor Plan" />
        </div>
      </div>
      `
    : '';
  
  const isLightAsset = financialParams 
        ? financialParams.leaseModel === 'light_asset' 
        : (managerIncomeY3 && managerIncomeY3 !== "N/A" && managerIncomeY3 !== "0" && managerIncomeY3 !== "0万元");

  const htmlTemplate = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${projectData.projectName} - 资产经营策划研报 (IC Memo)</title>
    <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&family=Noto+Serif+SC:wght@500;700&display=swap" rel="stylesheet">
    <style>
        :root {
            --primary-color: #0f172a;
            --accent-color: #2563eb;
            --bg-color: #f8fafc;
            --text-color: #334155;
            --sidebar-width: 280px;
        }
        body {
            font-family: 'Inter', sans-serif;
            background-color: var(--bg-color);
            color: var(--text-color);
            margin: 0;
            display: flex;
            min-height: 100vh;
        }
        
        /* Sidebar */
        .sidebar {
            width: var(--sidebar-width);
            background-color: white;
            border-right: 1px solid #e2e8f0;
            position: fixed;
            height: 100vh;
            overflow-y: auto;
            padding: 2rem;
            box-sizing: border-box;
            z-index: 10;
        }
        .logo {
            font-family: 'Noto Serif SC', serif;
            font-weight: 700;
            font-size: 1.1rem;
            color: var(--primary-color);
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid #f1f5f9;
        }
        .nav-group-title {
            font-size: 0.75rem;
            color: #94a3b8;
            font-weight: 700;
            text-transform: uppercase;
            margin: 1.5rem 0 0.5rem 0.5rem;
            letter-spacing: 0.05em;
        }
        .nav-item {
            display: block;
            padding: 0.75rem 1rem;
            margin-bottom: 0.5rem;
            border-radius: 0.5rem;
            color: #64748b;
            text-decoration: none;
            transition: all 0.2s;
            font-size: 0.9rem;
            font-weight: 500;
            cursor: pointer;
        }
        .nav-item:hover, .nav-item.active {
            background-color: #f1f5f9;
            color: var(--accent-color);
        }

        /* Main Content */
        .main {
            margin-left: var(--sidebar-width);
            flex: 1;
            padding: 3rem 4rem;
            max-width: 1200px;
        }

        header {
            margin-bottom: 3rem;
        }
        h1.project-title {
            font-family: 'Noto Serif SC', serif;
            font-size: 2.2rem;
            color: var(--primary-color);
            margin: 0 0 0.5rem 0;
            line-height: 1.2;
        }
        .subtitle {
            color: #64748b;
            font-size: 1rem;
            margin-top: 0.5rem;
        }
        .location-badge {
            display: inline-block;
            background: #e2e8f0;
            padding: 0.25rem 0.75rem;
            border-radius: 99px;
            font-size: 0.8rem;
            color: #475569;
            margin-right: 0.5rem;
        }

        /* Executive Summary Cards */
        .dashboard-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1.5rem;
            margin-bottom: 3rem;
        }
        .card {
            background: white;
            padding: 1.5rem;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            transition: transform 0.2s;
        }
        .card:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
        .card-label { font-size: 0.8rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
        .card-value { font-size: 1.6rem; font-weight: 700; color: var(--primary-color); margin-top: 0.5rem; }
        .card-sub { font-size: 0.85rem; color: #64748b; margin-top: 0.25rem; }
        
        .card.highlight { border: 1px solid #bfdbfe; background: #eff6ff; }
        .card.highlight .card-value { color: #1d4ed8; }

        /* Report Sections */
        .section {
            background: white;
            border-radius: 1rem;
            padding: 3rem;
            margin-bottom: 2rem;
            border: 1px solid #e2e8f0;
            display: none; /* Hidden by default for tab logic */
            animation: fadeIn 0.3s ease-in-out;
            box-shadow: none;
        }
        .section.active { display: block; }
        
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        /* Markdown Styling - Enhanced Readability */
        .markdown-content h1 { font-size: 1.8rem; margin-top: 0; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid #e2e8f0; font-family: 'Noto Serif SC', serif; }
        .markdown-content h2 { font-size: 1.5rem; color: #1e293b; margin-top: 2.5rem; margin-bottom: 1.25rem; font-weight: 700; border-left: none; padding-left: 0; }
        .markdown-content h3 { font-size: 1.25rem; color: #334155; margin-top: 2rem; margin-bottom: 1rem; font-weight: 600; }
        .markdown-content p { line-height: 1.7; color: #334155; margin-bottom: 1.25rem; font-size: 1rem; text-align: left; letter-spacing: normal; }
        .markdown-content strong { color: #000000; font-weight: 700; background: transparent; padding: 0 2px; border-bottom: 1px dashed #94a3b8; }
        .markdown-content ul { padding-left: 1.5rem; margin-bottom: 1.5rem; }
        .markdown-content li { margin-bottom: 0.75rem; color: #334155; }
        .markdown-content blockquote { border-left: 4px solid var(--accent-color); background: #f1f5f9; padding: 1.5rem; margin: 2rem 0; border-radius: 0 0.5rem 0.5rem 0; color: #475569; font-style: italic; }
        
        /* Highlight Numbers */
        .markdown-content strong:contains("¥"), .markdown-content strong:contains("%") {
             font-size: 1.1em;
             color: var(--accent-color);
        }

        /* Table Styling */
        .markdown-content table {
            display: block; /* Required for horizontal scroll in this raw layout */
            width: 100%;
            overflow-x: auto;
            border-collapse: collapse;
            margin: 2.5rem 0;
            font-size: 0.9rem; /* Slightly larger in report, smaller than headers */
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            border-radius: 0.5rem;
            table-layout: auto;
        }
        .markdown-content th {
            background-color: #f8fafc;
            color: #475569;
            font-weight: 600;
            text-align: left;
            padding: 1rem;
            border-bottom: 2px solid #e2e8f0;
            text-transform: uppercase;
            font-size: 0.75rem;
            white-space: nowrap;
        }
        .markdown-content td {
            padding: 1rem;
            border-bottom: 1px solid #e2e8f0;
            color: #334155;
            vertical-align: top;
            min-width: 120px; /* FORCE MIN WIDTH TO PREVENT SQUEEZING */
        }
        /* No bold underlining in tables for cleaner report */
        .markdown-content td strong { border-bottom: none; }
        
        .markdown-content tr:last-child td { border-bottom: none; }
        .markdown-content tr:hover td { background-color: #fcfcfc; }
        
        @media print {
            .sidebar { display: none; }
            .main { margin: 0; padding: 20px; }
            .section { display: block !important; break-inside: avoid; border: none; padding: 0; margin-bottom: 4rem; }
            .dashboard-grid { break-inside: avoid; }
            .nav-group-title { display: none; }
        }
    </style>
</head>
<body>

    <!-- Sidebar Navigation -->
    <div class="sidebar">
        <div class="logo">长租公寓资产策划顾问</div>
        <nav id="nav-container">
            <a class="nav-item active" onclick="switchTab('dashboard')">📝 投资决策建议书 (IC Memo)</a>
            
            <div class="nav-group-title">详细分析附录</div>
            ${supportingSteps.map((step) => `
                <a class="nav-item" onclick="switchTab('step-${step.stepId}')">
                    ${step.title.includes(':') ? step.title.split(':')[1].trim() : step.title}
                </a>
            `).join('')}
        </nav>
    </div>

    <!-- Main Content -->
    <div class="main">
        <header>
            <h1 class="project-title">${projectData.projectName}</h1>
            <div class="subtitle">
                <span class="location-badge">📍 ${projectData.location}</span>
                <span>生成日期: ${new Date().toLocaleDateString()}</span>
            </div>
        </header>

        <!-- Dashboard / Executive Summary -->
        <div class="dashboard-grid" id="dashboard">
             <div class="card highlight">
                <div class="card-label">NOI (稳态净营运收入)</div>
                <div class="card-value">${formatValue(isLightAsset ? (ownerIncomeY3 || noi) : noi, '万元/年')}</div>
                <div class="card-sub">${isLightAsset ? '业主净收入 (Year 3)' : 'Year 3 Net Operating Income'}</div>
             </div>
             
             <div class="card">
                <div class="card-label">IRR (投资回报率)</div>
                <div class="card-value">${formatValue(irr, '%')}</div>
                <div class="card-sub">Internal Rate of Return</div>
             </div>

             <div class="card">
                 <div class="card-label">投资回收期</div>
                 <div class="card-value">${formatValue(payback, '年')}</div>
                 <div class="card-sub">Payback Period</div>
             </div>
             
             <div class="card">
                 <div class="card-label">综合坪效</div>
                 <div class="card-value">${formatValue(efficiency, '元/平/月')}</div>
                 <div class="card-sub">Revenue per SQM</div>
             </div>
             
             ${isLightAsset ? `
             <div class="card">
                <div class="card-label">管理费收入 (Year 3)</div>
                <div class="card-value">${formatValue(managerIncomeY3, '万元/年')}</div>
                <div class="card-sub">Manager Fee Income</div>
             </div>
             ` : ''}
        </div>

        <!-- Report Content Sections -->
        <div class="content-body">
            <!-- IC Memo Section (Always First) -->
            <div id="dashboard-content" class="section active">
                <div class="markdown-container" data-markdown="${encodeURIComponent(memoStep?.content || '')}"></div>
                ${imageHtml}
            </div>

            <!-- Detailed Steps -->
            ${supportingSteps.map(step => `
                <div id="step-${step.stepId}" class="section">
                    <h2 style="border-bottom: 2px solid #f1f5f9; padding-bottom: 1rem; margin-bottom: 2rem;">${step.title}</h2>
                    <div class="markdown-container" data-markdown="${encodeURIComponent(step.content)}"></div>
                </div>
            `).join('')}
        </div>
        
        <footer style="margin-top: 5rem; border-top: 1px solid #e2e8f0; padding-top: 2rem; color: #94a3b8; font-size: 0.8rem; text-align: center;">
            <p>Generated by AI Asset Manager Pro • © ${new Date().getFullYear()}</p>
        </footer>

    </div>

    <script>
        // Render Markdown on load
        window.addEventListener('DOMContentLoaded', () => {
            if (typeof marked !== 'undefined') {
                document.querySelectorAll('.markdown-container').forEach(el => {
                    const raw = decodeURIComponent(el.getAttribute('data-markdown'));
                    el.innerHTML = marked.parse(raw);
                });
            }
        });

        function switchTab(tabId) {
            // Update Sidebar
            document.querySelectorAll('.nav-item').forEach(el => el.classList.remove('active'));
            event.currentTarget.classList.add('active');

            // Update Content
            
            const isDashboard = tabId === 'dashboard';
            
            // Toggle Dashboard Grid
            const grid = document.getElementById('dashboard');
            if (grid) grid.style.display = isDashboard ? 'grid' : 'none';

            // Hide all sections
            document.querySelectorAll('.section').forEach(el => el.classList.remove('active'));

            if (isDashboard) {
                document.getElementById('dashboard-content').classList.add('active');
            } else {
                const target = document.getElementById(tabId);
                if (target) target.classList.add('active');
            }
            
            // Scroll to top
            window.scrollTo(0,0);
        }
    </script>
</body>
</html>
  `;
  
  return htmlTemplate;
};